// Frontend validator for the API-key IP allowlist field. Accepts a single
// IPv4/IPv6 address, optionally with a CIDR prefix. Backend ultimately uses
// ip-range-check so the wire format is one address-or-CIDR per entry.

export function isValidIpOrCidr(input: string): boolean {
  const value = input.trim();
  if (!value) return false;
  const slashCount = (value.match(/\//g) || []).length;
  if (slashCount > 1) return false;
  const [addr, prefix] = value.split("/");

  const v4 = isValidIPv4(addr);
  const v6 = !v4 && isValidIPv6(addr);
  if (!v4 && !v6) return false;

  if (prefix !== undefined) {
    if (!/^\d+$/.test(prefix)) return false;
    const n = Number(prefix);
    if (v4 && (n < 0 || n > 32)) return false;
    if (v6 && (n < 0 || n > 128)) return false;
  }
  return true;
}

export function canonicalizeIpOrCidr(input: string): string {
  const value = input.trim();
  if (!value.includes("/")) return value;
  const [addr, prefixStr] = value.split("/");
  if (!isValidIPv4(addr)) return value;
  const prefix = Number(prefixStr);
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) return value;

  const octets = addr.split(".").map(Number);
  const asInt =
    ((octets[0] << 24) | (octets[1] << 16) | (octets[2] << 8) | octets[3]) >>>
    0;
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  const network = (asInt & mask) >>> 0;
  const canonical = [
    (network >>> 24) & 0xff,
    (network >>> 16) & 0xff,
    (network >>> 8) & 0xff,
    network & 0xff,
  ].join(".");
  return `${canonical}/${prefix}`;
}

export function buildIpSuggestions(
  draft: string,
  excludeList: string[],
): string[] {
  const value = draft.trim();
  if (!value) return [];

  if (value.includes("/")) {
    if (!isValidIpOrCidr(value)) return [];
    const canonical = canonicalizeIpOrCidr(value);
    return excludeList.includes(canonical) ? [] : [canonical];
  }

  const octets = value.split(".");
  const filledOctets = octets.filter((o) => o !== "");
  if (filledOctets.length < 3) return [];
  if (
    !filledOctets.every(
      (o) => /^\d+$/.test(o) && Number(o) >= 0 && Number(o) <= 255,
    )
  ) {
    return [];
  }

  const hasFourthOctet = octets.length === 4 && octets[3] !== "";
  const baseIp = hasFourthOctet
    ? value
    : [octets[0], octets[1], octets[2], "0"].join(".");
  if (!isValidIpOrCidr(baseIp)) return [];

  const candidates = new Set<string>();
  if (hasFourthOctet) candidates.add(baseIp);
  for (const prefix of [24, 16, 8]) {
    candidates.add(canonicalizeIpOrCidr(`${baseIp}/${prefix}`));
  }
  return Array.from(candidates).filter((c) => !excludeList.includes(c));
}

function isValidIPv4(addr: string): boolean {
  const parts = addr.split(".");
  if (parts.length !== 4) return false;
  return parts.every(
    (p) => /^\d+$/.test(p) && Number(p) >= 0 && Number(p) <= 255,
  );
}

function isValidIPv6(addr: string): boolean {
  if (!/^[0-9a-fA-F:]+$/.test(addr)) return false;
  // 3+ consecutive colons are never valid (e.g. ":::1").
  if (/:::/.test(addr)) return false;
  // A leading or trailing single colon is only valid as part of "::".
  if (addr.startsWith(":") && !addr.startsWith("::")) return false;
  if (addr.endsWith(":") && !addr.endsWith("::")) return false;
  // Can't have more than 1 "::"
  const doubleColons = (addr.match(/::/g) || []).length;
  if (doubleColons > 1) return false;
  const groups = addr.split(":").filter((p) => p !== "");
  if (doubleColons === 0 && groups.length !== 8) return false;
  if (doubleColons === 1 && groups.length > 7) return false;
  return groups.every((p) => /^[0-9a-fA-F]{1,4}$/.test(p));
}
